import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { signalLogout } from '../auth';
import { apiFetch, setCsrfToken } from '../api';
import LandingPage from './LandingPage';

// ── Auth context — exposes current user, logout, and refreshUser ─────────────
export const AuthContext = createContext({ user: null, logout: () => {}, refreshUser: () => {} });
export const useAuth = () => useContext(AuthContext);

// ── LoginGate ─────────────────────────────────────────────────────────────────
// Renders LandingPage (with AuthModal) when unauthenticated.
// Renders children wrapped in AuthContext once authenticated.
//
// States: 'checking' | 'authed' | 'login' | 'signup'

export default function LoginGate({ children }) {
  const [state, setState] = useState('checking');
  const [user,  setUser]  = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(async r => {
        if (r.ok) {
          const data = await r.json();
          setCsrfToken(data.csrf_token); // Prime CSRF token from session
          setUser(data);
          setState('authed');
        } else {
          setState('login');
        }
      })
      .catch(() => setState('login'));
  }, []);

  // Re-validate session when the page is restored from bfcache (browser back/forward).
  // Without this, bfcache restores the frozen React state from the previous session,
  // causing a different user's UI to render with the wrong account.
  useEffect(() => {
    function onPageShow(e) {
      if (!e.persisted) return; // normal load — already handled above
      fetch('/api/auth/me', { credentials: 'include' })
        .then(async r => {
          if (r.ok) {
            const data = await r.json();
            setCsrfToken(data.csrf_token);
            setUser(data);
            setState('authed');
          } else {
            setUser(null); setState('login');
          }
        })
        .catch(() => { setUser(null); setState('login'); });
    }
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Listen for 401s fired by the fetch interceptor
  useEffect(() => {
    const onLogout = () => { setUser(null); setState('login'); };
    window.addEventListener('auth:logout', onLogout);
    return () => window.removeEventListener('auth:logout', onLogout);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const r = await fetch('/api/auth/me', { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setCsrfToken(data.csrf_token);
        setUser(data);
      }
    } catch (_) {}
  }, []);

  const logout = useCallback(async () => {
    try { await apiFetch('/api/auth/logout', { method: 'POST' }); } catch (_) {}
    setUser(null);
    setState('login');
    signalLogout();
  }, []);

  function onAuthSuccess(u) {
    setUser(u);
    setState('authed');
  }

  // ── Loading spinner ─────────────────────────────────────────────────────────
  if (state === 'checking') {
    return (
      <div className="login-gate">
        <div className="login-gate-card login-gate-card--checking">
          <div className="login-gate-spinner" />
        </div>
      </div>
    );
  }

  // ── Authenticated ───────────────────────────────────────────────────────────
  if (state === 'authed') {
    return (
      <AuthContext.Provider value={{ user, logout, refreshUser }}>
        {children}
      </AuthContext.Provider>
    );
  }

  // ── Unauthenticated — show landing page with auth modal ─────────────────────
  const reMode = localStorage.getItem('re_mode') === 'true' ||
    new URLSearchParams(window.location.search).get('mode') === 're';

  return (
    <LandingPage
      onAuthSuccess={onAuthSuccess}
      initialAuthTab={state === 'signup' ? 'signup' : null}
      reMode={reMode}
    />
  );
}
